import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Combobox de selección de precio para líneas de venta.
 *
 * Modos disponibles (cualquiera con valor numérico > 0 aparece):
 *  - P.S.   = Precio fijo / Sugerido (PVP máx)            -> options.precio_fijo
 *  - P.M.   = Precio del Margen (calculado por margen del cliente/producto)
 *                                                          -> options.precio_margen
 *  - P.Min  = Precio Mínimo permitido (piso negociación)  -> options.precio_minimo
 *  - P.P.   = Precio Personalizado (input libre)          -> siempre disponible
 *
 * Props:
 *  - value (number)           : precio actual (se vinculará al modo más cercano)
 *  - onChange (number) => void
 *  - options { precio_fijo?, precio_margen?, precio_minimo? }
 *  - decimals (default 7)     : decimales soportados en el input
 *  - testId
 *  - belowMin (bool)          : marca visualmente que el precio actual está bajo el mínimo
 *  - className
 *  - inputClassName
 */
export const PriceCombobox = ({
  value,
  onChange,
  options = {},
  decimals = 7,
  testId,
  belowMin = false,
  className = "",
  inputClassName = "",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Cerrar al hacer click afuera
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const items = useMemo(() => {
    const out = [];
    if (options.precio_fijo != null && Number(options.precio_fijo) > 0) {
      out.push({
        key: "ps",
        label: "P.S.",
        title: "Precio fijo (PVP sugerido)",
        valor: Number(options.precio_fijo),
        color: "#205c45",
      });
    }
    if (options.precio_margen != null && Number(options.precio_margen) > 0) {
      out.push({
        key: "pm",
        label: "P.M.",
        title: "Precio del margen del cliente",
        valor: Number(options.precio_margen),
        color: "#2d4a73",
      });
    }
    if (options.precio_minimo != null && Number(options.precio_minimo) > 0) {
      out.push({
        key: "pmin",
        label: "P.Min",
        title: "Precio mínimo permitido",
        valor: Number(options.precio_minimo),
        color: "#8a5500",
      });
    }
    return out;
  }, [options]);

  const fmt = (n) => {
    if (n == null) return "—";
    const trim = (+n).toFixed(decimals).replace(/\.?0+$/, "");
    return trim || "0";
  };

  const handlePick = (v) => {
    onChange(+(+v).toFixed(decimals));
    setOpen(false);
  };

  // Detectar qué modo coincide con el valor actual (para destacar)
  const activeKey = useMemo(() => {
    const v = +value;
    if (!Number.isFinite(v)) return null;
    for (const it of items) {
      if (Math.abs(it.valor - v) < 1e-6) return it.key;
    }
    return "pp";
  }, [items, value]);

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <div className="flex items-stretch gap-0">
        <input
          type="number"
          step={`0.${"0".repeat(decimals - 1)}1`}
          value={value ?? 0}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className={`h-9 px-2 text-right tabular-nums w-full rounded-l border ${
            belowMin ? "border-[#b53a25] bg-[#fdecea]" : "border-[#d1dbd6] bg-white"
          } focus:outline-none focus:ring-2 focus:ring-[#205c45] focus:border-transparent ${inputClassName}`}
          disabled={disabled}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <button
          type="button"
          onClick={() => !disabled && setOpen((o) => !o)}
          className={`px-1.5 rounded-r border border-l-0 ${
            belowMin ? "border-[#b53a25] bg-[#fdecea] text-[#b53a25]" : "border-[#d1dbd6] bg-[#f7f9f8] text-[#5c7368] hover:bg-[#e9f2ee]"
          }`}
          title="Elegir nivel de precio"
          disabled={disabled}
          data-testid={testId ? `${testId}-trigger` : undefined}
        >
          <ChevronDown size={14} />
        </button>
      </div>
      {/* Etiqueta del modo activo */}
      <div className="absolute -top-3 right-1 pointer-events-none">
        <span className={`text-[9px] font-bold px-1 rounded ${belowMin ? "bg-[#b53a25] text-white" : "bg-[#e9f2ee] text-[#205c45]"}`}>
          {activeKey === "ps" && "P.S."}
          {activeKey === "pm" && "P.M."}
          {activeKey === "pmin" && "P.Min"}
          {activeKey === "pp" && "P.P."}
        </span>
      </div>

      {open && !disabled && (
        <div
          className="absolute right-0 z-50 mt-1 w-64 rounded-md border border-[#d1dbd6] bg-white shadow-lg overflow-hidden"
          data-testid={testId ? `${testId}-menu` : undefined}
        >
          <div className="px-3 py-1.5 bg-[#f7f9f8] border-b border-[#d1dbd6] text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">
            Niveles de precio disponibles
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-3 py-2 text-xs text-[#8ba397] italic">Producto sin precios configurados</li>
            )}
            {items.map((it) => (
              <li key={it.key}>
                <button
                  type="button"
                  onClick={() => handlePick(it.valor)}
                  className={`w-full text-left px-3 py-2 hover:bg-[#e9f2ee] flex items-center justify-between gap-2 ${
                    activeKey === it.key ? "bg-[#e9f2ee]" : ""
                  }`}
                  data-testid={testId ? `${testId}-opt-${it.key}` : undefined}
                >
                  <span>
                    <span
                      className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded mr-2"
                      style={{ backgroundColor: `${it.color}1a`, color: it.color }}
                    >
                      {it.label}
                    </span>
                    <span className="text-xs text-[#4a5c53]">{it.title}</span>
                  </span>
                  <span className="font-mono tabular-nums font-semibold text-[#15241d]">${fmt(it.valor)}</span>
                </button>
              </li>
            ))}
            <li className="border-t border-[#d1dbd6]">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={`w-full text-left px-3 py-2 hover:bg-[#fef7ec] flex items-center gap-2 ${
                  activeKey === "pp" ? "bg-[#fef7ec]" : ""
                }`}
                data-testid={testId ? `${testId}-opt-pp` : undefined}
              >
                <span className="inline-block text-[10px] font-bold px-1.5 py-0.5 rounded mr-2 bg-[#fef7ec] text-[#8a5500]">
                  P.P.
                </span>
                <span className="text-xs text-[#4a5c53]">
                  Personalizado · escriba el valor en el campo
                </span>
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PriceCombobox;
